"use client"

import { signIn } from "next-auth/react"
import { useSearchParams } from "next/navigation"
import { Suspense } from "react"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Layers, Share2, CheckCircle } from "lucide-react"

const LoginContent = () => {
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get("callbackUrl") || "/"
  const error = searchParams.get("error")

  return (
    <div className="flex items-center justify-center w-full min-h-[80vh] max-w-4xl">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Welcome to Voyas</CardTitle>
          <CardDescription>
            Sign in to start planning your next adventure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {error && (
              <p className="text-sm text-red-500 text-center">
                {error === "AccessDenied" 
                  ? "You do not have access to this resource." 
                  : "An error occurred while signing in."}
              </p>
            )}
            
            <Button
              className="w-full h-12 gap-2 text-base bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
              onClick={() => signIn("google", { callbackUrl })}
            >
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Why Sign In?
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 text-sm text-muted-foreground">
              <div className="flex gap-2">
                <Layers className="h-5 w-5" />
                <span>Save and manage your travel plans</span>
              </div>
              <div className="flex gap-2">
                <Share2 className="h-5 w-5" />
                <span>Share itineraries with friends and family</span>
              </div>
              <div className="flex gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Get personalized travel recommendations</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default function LoginPage() {
  return (
    <div className="flex items-center justify-center w-full">
    <Suspense fallback={
      <div className="flex items-center justify-center min-h-[80vh] w-full max-w-4xl">
        <Card className="w-full p-8">
          <div className="flex justify-center">
            Loading...
          </div>
        </Card>
      </div>
    }>
      <LoginContent />
    </Suspense>
    </div>
  )
}